import { FunctionComponent, useCallback, useEffect, useRef } from 'react';

import { useCoreDispatch } from '../../store/CoreStore';
import { useGameStateSlice } from '../../store/slices/game/selectors';
import { useSettingsSlice } from '../../store/slices/settings/selectors';
import { settingsActions } from '../../store/slices/settings/slice';
import { AudioContext } from '../../utils/polyfills/audioContext';

import { AudioEventStopDetailType, AudioEventPlayDetailType, AudioEvents, SoundResourceType } from './types';

const audioContext = AudioContext ? new AudioContext() : null;
export const AudioControllerConnected: FunctionComponent<{ audioFiles: SoundResourceType[] }> = ({ audioFiles }) => {
  const channelVolumes = useSettingsSlice.getChannelVolumes();
  const dispatch = useCoreDispatch();
  const channel = useSettingsSlice.getMasterChannel();
  const uiChannel = useSettingsSlice.getUIVolumeChannel();
  const isGameSuspended = useGameStateSlice.getIsGameSuspended();

  const audioInstancesRef = useRef<Record<string, AudioBuffer>>({});
  const loadingAudioRef = useRef<Array<string>>([]);

  const activeAudioSources = useRef<Record<string, AudioBufferSourceNode[]>>({});
  const channels = useRef<Record<string, GainNode>>({});

  useEffect(() => {
    for (const channelName in channelVolumes) {
      const channelNode = channels.current[channelName];
      if (channelNode) {
        channelNode.gain.value = channelVolumes[channelName];
      } else {
        if (!audioContext) return;
        const audioGain = audioContext.createGain();
        audioGain.gain.value = channelVolumes[channelName];
        channels.current[channelName] = audioGain;
      }
    }
  }, [channelVolumes]);

  const getChannel = (name: string): GainNode => channels.current[name];
  const getChannelVolume = (name: string): number => channels.current[name]?.gain.value || 0;

  const play = useCallback(
    (channel: string, audioName: string, loop: boolean = false): void => {
      if (isGameSuspended || !audioContext) return;
      const audioSource = audioContext.createBufferSource();
      const audioGain = getChannel(channel);
      audioSource.buffer = audioInstancesRef.current[audioName];
      audioSource.connect(audioGain).connect(audioContext.destination);

      audioSource.loop = loop;
      audioSource.start(0);

      if (!activeAudioSources.current[audioName]) {
        activeAudioSources.current[audioName] = [];
      }

      audioSource.onended = (): void => {
        if (activeAudioSources.current[audioName].length > 1) {
          activeAudioSources.current[audioName].shift();
        } else {
          delete activeAudioSources.current[audioName];
        }
      };

      activeAudioSources.current[audioName].push(audioSource);
    },
    [isGameSuspended]
  );

  const fetchFile = useCallback(
    (audioFiles: SoundResourceType[], chanel: string, audioName: string, loop: boolean = false): void => {
      for (const audioFile of audioFiles) {
        if (audioFile.name === audioName && !loadingAudioRef.current.includes(audioName)) {
          loadingAudioRef.current.push(audioName);
          fetch(audioFile.url)
            .then((response) => response.arrayBuffer())
            .then((buffer) => {
              try {
                if (!audioContext) throw new Error('AudioContext is not supported');
                return audioContext.decodeAudioData(buffer);
              } catch (e) {
                console.error(`[AUDIO] Error decoding audio data: ${e}`);
                return Promise.reject(e);
              }
            })
            .then((audioBuffer) => {
              loadingAudioRef.current = loadingAudioRef.current.filter((item) => item !== audioName);
              const instances = { ...audioInstancesRef.current };
              instances[audioName] = audioBuffer;
              audioInstancesRef.current = { ...audioInstancesRef.current, ...instances };
              play(chanel, audioName, loop);
            });
        }
      }
    },
    [play]
  );

  const handlePlayAudio = useCallback(
    (event: CustomEvent<AudioEventPlayDetailType>) => {
      const audioName = event.detail.name;
      const channel = event.detail.channel || uiChannel;
      if (!channel) {
        console.log(`[AUDIO] No default UI channel is found`);
        return;
      }
      const currentAudio = audioInstancesRef.current[audioName];
      const channelVolume = getChannelVolume(channel);

      if (channelVolume > 0) {
        if (currentAudio) {
          play(channel, audioName, event.detail.loop);
        } else {
          fetchFile(audioFiles, channel, audioName, event.detail.loop);
        }
      }
    },
    [audioFiles, isGameSuspended, uiChannel]
  );

  const handleStopAudio = useCallback((event: CustomEvent<AudioEventStopDetailType>) => {
    const audioName = event.detail.name;
    const audioSource = activeAudioSources.current[audioName]?.shift();
    if (audioSource) {
      audioSource.stop();
    }
  }, []);

  const handleVisibilityChange = useCallback(() => {
    const isVisible = document.visibilityState === 'visible';
    dispatch(settingsActions.setIsGameVisible(isVisible));
  }, [dispatch, channel]);

  // Bug in iOS where losing focus bugs audiocontext. It has to be suspended and resumed manually for it to work. LCE-72359
  const handleFocus = useCallback(() => {
    if (!audioContext) return;
    audioContext.suspend();
    audioContext.resume();
  }, [audioContext]);

  useEffect(() => {
    window.addEventListener(AudioEvents.PLAY, handlePlayAudio as EventListener);
    window.addEventListener(AudioEvents.STOP, handleStopAudio as EventListener);
    document.addEventListener('visibilitychange', handleVisibilityChange as EventListener);
    window.addEventListener('pageshow', handleFocus as EventListener);
    window.addEventListener('focus', handleFocus as EventListener);
    return () => {
      window.removeEventListener(AudioEvents.PLAY, handlePlayAudio as EventListener);
      window.removeEventListener(AudioEvents.STOP, handleStopAudio as EventListener);
      document.removeEventListener('visibilitychange', handleVisibilityChange as EventListener);
      window.removeEventListener('pageshow', handleFocus as EventListener);
      window.removeEventListener('focus', handleFocus as EventListener);
    };
  }, [handlePlayAudio, handleStopAudio, handleVisibilityChange]);

  return null;
};
